import { useState } from 'react'
import { CreditCard, Crown, ExternalLink, Loader2, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { useBillingStatus, useBillingCheckout, useBillingPortal, useCurrentUser } from '@/hooks/useApi'

const PLAN_FEATURES: Record<string, string[]> = {
  standard: ['2 seats', 'All AI tools', 'Email support', 'Approval workflows'],
  team: ['5 seats', 'All AI tools', 'Priority support', 'Manager roles', 'Deploy approvals'],
  agency: ['10 seats', 'All AI tools', 'Dedicated support', 'All roles', 'GitHub integration'],
}

function pence(p: number): string {
  if (p === 0) return 'Free'
  return `£${(p / 100).toFixed(2)}`
}

type PlanRow = {
  plan_id: string; plan_name: string; seats: number;
  price_monthly_pence: number; price_launch_pence: number; price_annual_pence: number;
}

type SubRow = {
  plan_id: string; plan_name: string; billing_cycle: string;
  is_founding_member: number; status: string; cancel_at_period_end: number;
  current_period_end: string | null;
}

type FmWindow = {
  is_open: number; current_count: number; max_customers: number;
  opened_at: string; closes_at: string | null;
}

export function BillingPanel() {
  const currentUser = useCurrentUser()
  const { data, isLoading } = useBillingStatus()
  const checkoutMutation = useBillingCheckout()
  const portalMutation = useBillingPortal()
  const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly')
  const [checkoutError, setCheckoutError] = useState('')
  const [portalError, setPortalError] = useState('')

  const isOwner = currentUser.tenant_role === 'owner'
  const sub = (data?.subscription ?? null) as SubRow | null
  const fmWindow = (data?.founding_member_window ?? null) as FmWindow | null
  const plans = (data?.plans ?? []) as PlanRow[]

  const currentPlan = sub?.plan_id ?? currentUser.plan ?? 'free'
  const isFoundingMember = !!(sub?.is_founding_member)
  const fmOpen = !!(fmWindow?.is_open) && (fmWindow?.current_count ?? 0) < (fmWindow?.max_customers ?? 100)
  const fmSpotsLeft = fmWindow ? fmWindow.max_customers - fmWindow.current_count : 0

  const handleUpgrade = async (planId: string) => {
    setCheckoutError('')
    try {
      const result = await checkoutMutation.mutateAsync({ planId, billingCycle: cycle })
      if (result.checkout_url) window.location.href = result.checkout_url
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'Checkout failed')
    }
  }

  const handlePortal = async () => {
    setPortalError('')
    try {
      const result = await portalMutation.mutateAsync()
      if (result.portal_url) window.open(result.portal_url, '_blank')
    } catch (e) {
      setPortalError(e instanceof Error ? e.message : 'Failed to open billing portal')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Billing</h2>
          {isFoundingMember && (
            <div className="flex items-center gap-1.5 mt-1">
              <Crown className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-xs text-amber-400 font-medium">Founding Member</span>
              <span className="text-xs text-text-muted">· pricing locked forever</span>
            </div>
          )}
        </div>
        {sub && isOwner && (
          <Button size="sm" variant="ghost" onClick={handlePortal} disabled={portalMutation.isPending}>
            {portalMutation.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
            Manage billing
          </Button>
        )}
      </div>

      {/* Current plan */}
      <Card className="p-4 bg-surface/30 border-border">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="h-4 w-4 text-text-muted" />
          <span className="text-sm font-medium text-text-primary">
            {sub ? `${sub.plan_name} Plan` : 'Free Plan'}
          </span>
          {sub && (
            <Badge variant={sub.status === 'active' ? 'success' : 'warning'} className="text-[10px]">
              {sub.status}
            </Badge>
          )}
          {sub?.cancel_at_period_end ? (
            <Badge variant="warning" className="text-[10px]">
              Cancels {new Date(sub.current_period_end ?? '').toLocaleDateString()}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-text-muted">
          {sub
            ? `${sub.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} billing${
                sub.current_period_end ? ` · renews ${new Date(sub.current_period_end).toLocaleDateString()}` : ''
              }`
            : 'Upgrade to unlock team features'}
        </p>
      </Card>

      {/* FM window banner */}
      {fmOpen && !isFoundingMember && (
        <Card className="p-4 bg-amber-500/5 border-amber-500/20">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-4 w-4 text-amber-400 shrink-0" />
            <span className="text-sm font-medium text-amber-400">Founding Member pricing available</span>
          </div>
          <p className="text-xs text-text-muted">
            {fmSpotsLeft} of {fmWindow?.max_customers} spots remaining.
            {' '}Price locked permanently — <span className="text-danger">cancelling loses FM status forever</span>.
          </p>
        </Card>
      )}

      {/* Cycle toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-text-muted">Billing:</span>
        <div className="flex rounded-lg border border-border overflow-hidden text-sm">
          <button
            onClick={() => setCycle('monthly')}
            className={`px-3 py-1.5 transition-colors ${
              cycle === 'monthly' ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setCycle('annual')}
            className={`px-3 py-1.5 transition-colors ${
              cycle === 'annual' ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Annual
            <span className="text-success text-[10px] ml-1">(save 2 mo)</span>
          </button>
        </div>
      </div>

      {/* Plan grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {plans.filter((p) => p.plan_id !== 'free').map((plan) => {
          const isCurrent = plan.plan_id === currentPlan
          const displayPrice = cycle === 'annual'
            ? Math.round(plan.price_annual_pence / 10)
            : (fmOpen ? plan.price_launch_pence : plan.price_monthly_pence)
          const features = PLAN_FEATURES[plan.plan_id] ?? []

          return (
            <Card
              key={plan.plan_id}
              className={`p-4 border transition-colors ${
                isCurrent ? 'border-accent/40 bg-accent/5' : 'border-border bg-surface/30'
              }`}
            >
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-text-primary text-sm">{plan.plan_name}</span>
                  {isCurrent && <Badge variant="default" className="text-[10px]">Current</Badge>}
                </div>
                <div className="text-xl font-bold text-text-primary">
                  {pence(displayPrice)}
                  <span className="text-xs font-normal text-text-muted ml-1">/mo</span>
                </div>
                {cycle === 'annual' && (
                  <p className="text-xs text-text-muted mt-0.5">{pence(plan.price_annual_pence)} billed annually</p>
                )}
                {fmOpen && cycle === 'monthly' && plan.price_launch_pence < plan.price_monthly_pence && (
                  <p className="text-xs text-amber-400 mt-0.5 flex items-center gap-1">
                    <Crown className="h-3 w-3" /> FM launch price
                  </p>
                )}
              </div>
              <div className="space-y-1.5 mb-4">
                {features.map((f) => (
                  <div key={f} className="flex items-center gap-1.5 text-xs text-text-muted">
                    <CheckCircle className="h-3 w-3 text-success shrink-0" />{f}
                  </div>
                ))}
              </div>
              {isOwner && !isCurrent && (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => handleUpgrade(plan.plan_id)}
                  disabled={checkoutMutation.isPending}
                >
                  {checkoutMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  Upgrade
                </Button>
              )}
            </Card>
          )
        })}
      </div>

      {checkoutError && <p className="text-sm text-danger">{checkoutError}</p>}
      {portalError && <p className="text-sm text-danger">{portalError}</p>}
      {!isOwner && (
        <p className="text-xs text-text-muted">Only workspace owners can manage billing.</p>
      )}
    </div>
  )
}
